const NO_ACTIVE_VIDEO_CODE = 'NoActiveVideo';

export function isVideoNotActiveError(error: unknown): boolean {
  const data = (error as { data?: { code?: unknown } } | null)?.data;
  return data?.code === NO_ACTIVE_VIDEO_CODE;
}

import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import type {
  DtlsParameters,
  IceCandidate,
  IceParameters,
  MediaKind,
  RtpCapabilities,
  RtpParameters,
  SctpParameters,
} from 'mediasoup-client/types';

export type CameraState = 'Enabled' | 'Disabled';
export type RecordingState = 'NotReady' | 'Ready';
export type CallerVideoConnectionState = 'NotConnected' | 'Connecting' | 'Connected' | 'Disconnected';

export type VideoState = {
  callerVideoConnectionState: CallerVideoConnectionState;
  recordingState: RecordingState;
  cameraState: CameraState;
  activeProducerId: string | null;
};

export type VideoRotation = 'clockwise' | 'counterclockwise' | 'rotate-180' | 'None';

export type TransportParams = {
  id: string;
  iceParameters: IceParameters;
  iceCandidates: IceCandidate[];
  dtlsParameters: DtlsParameters;
  sctpParameters?: SctpParameters;
};

export type ConsumerParams = {
  id: string;
  producerId: string;
  kind: MediaKind;
  rtpParameters: RtpParameters;
  videoRotation?: VideoRotation;
};

export type JoinRoomResult = {
  routerRtpCapabilities: RtpCapabilities;
  activeProducerId: string | null;
};

export type VideoServerEvents = {
  newProducerAvailable: (producerId: string) => void;
  producerStopped: (stoppedProducerId: string) => void;
  callerProducerReplaced: () => void;
  callerVideoConnectionStateChanged: (state: CallerVideoConnectionState) => void;
  recordingStateChanged: (state: RecordingState) => void;
  cameraStateChanged: (videoId: string, state: CameraState) => void;
};

type Ack<TResult> = { ok: true; result: TResult } | { ok: false; error: string };

export type VideoSignallingConnection = {
  readonly socket: Socket;
  connect(): Promise<void>;
  disconnect(): void;
  on<TEventName extends keyof VideoServerEvents>(
    eventName: TEventName,
    handler: VideoServerEvents[TEventName],
  ): () => void;
  onDisconnect(handler: (reason: string) => void): () => void;
  onReconnect(handler: () => void): () => void;

  joinRoom(): Promise<JoinRoomResult>;
  createWebRtcTransport(): Promise<TransportParams>;
  connectWebRtcTransport(dtlsParameters: unknown): Promise<void>;
  produce(kind: 'audio' | 'video', rtpParameters: unknown): Promise<string>;
  consume(producerId: string, rtpCapabilities: unknown): Promise<ConsumerParams>;
  pauseConsumer(): Promise<void>;
  resumeConsumer(): Promise<void>;
  getVideoState(): Promise<VideoState>;
  reportCameraState(state: CameraState): Promise<void>;
  sendConsumerStats(stats: unknown): Promise<void>;
  sendProducerStats(stats: unknown): Promise<void>;
  sendClientEvent(payload: unknown): Promise<void>;
};

export type VideoSignallingOptions = {
  videoServerUrl: string;
  videoId: string;
  accessTokenFactory?: () => Promise<string>;
};

export function createVideoSignallingConnection(options: VideoSignallingOptions): VideoSignallingConnection {
  const videoServerUrl = options.videoServerUrl.replace(/\/+$/, '');

  const socket = io(videoServerUrl, {
    path: '/video-signalling',
    transports: ['websocket'],
    autoConnect: false,
    auth: (callback: (data: Record<string, unknown>) => void) => {
      const withToken = (token?: string) => callback({ videoId: options.videoId, token });

      if (options.accessTokenFactory === undefined) {
        withToken();
        return;
      }

      void options.accessTokenFactory().then(withToken, () => withToken());
    },
  });

  function request<TResult>(eventName: string, ...args: unknown[]): Promise<TResult> {
    return new Promise<TResult>((resolve, reject) => {
      socket.emit(eventName, ...args, (response: Ack<TResult>) => {
        if (response?.ok) {
          resolve(response.result);
        } else {
          reject(new Error(response?.error ?? `${eventName} failed.`));
        }
      });
    });
  }

  return {
    socket,
    connect() {
      return new Promise<void>((resolve, reject) => {
        const onConnect = () => {
          socket.off('connect_error', onError);
          resolve();
        };
        const onError = (error: Error) => {
          socket.off('connect', onConnect);
          reject(error);
        };

        socket.once('connect', onConnect);
        socket.once('connect_error', onError);
        socket.connect();
      });
    },
    disconnect: () => void socket.disconnect(),
    on(eventName, handler) {
      socket.on(eventName as string, handler as never);
      return () => void socket.off(eventName as string, handler as never);
    },
    onDisconnect(handler) {
      socket.on('disconnect', handler);
      return () => void socket.off('disconnect', handler);
    },
    onReconnect(handler) {
      socket.io.on('reconnect', handler);
      return () => void socket.io.off('reconnect', handler);
    },

    joinRoom: () => request<JoinRoomResult>('joinRoom'),
    createWebRtcTransport: () => request<TransportParams>('createWebRtcTransport'),
    connectWebRtcTransport: (dtlsParameters) => request<void>('connectWebRtcTransport', dtlsParameters),
    produce: (kind, rtpParameters) => request<string>('produce', kind, rtpParameters),
    consume: (producerId, rtpCapabilities) => request<ConsumerParams>('consume', producerId, rtpCapabilities),
    pauseConsumer: () => request<void>('pauseConsumer'),
    resumeConsumer: () => request<void>('resumeConsumer'),
    getVideoState: () => request<VideoState>('getVideoState'),
    reportCameraState: (state) => request<void>('reportCameraState', state),
    sendConsumerStats: (stats) => request<void>('sendConsumerStats', stats),
    sendProducerStats: (stats) => request<void>('sendProducerStats', stats),
    sendClientEvent: (payload) => request<void>('sendClientEvent', payload),
  };
}
